/**
 * What this server can do, as it describes itself at `/api/capabilities`.
 *
 * Fetched once per page load and kept, because two very different places need
 * it: the shell, which decides which warning banners to show, and the call,
 * which decides whether a recon run is on. Two fetches of the same immutable
 * answer would have been simpler to write and would have made the second
 * reader wait on the network in the middle of starting a call.
 *
 * A failure resolves to `null` rather than rejecting. Every one of these flags
 * is a refinement of the interface — none of them is permission to make a
 * call — so a server that will not answer this question must not stop one.
 */

export interface Capabilities {
  provider: {
    name: string;
    configured: boolean;
    /** Optional for the same reason as `recon` below: an older instance. */
    sourceTranscription?: boolean;
  };
  turn: { configured: boolean; forced: boolean };
  accessCodeRequired: boolean;
  /**
   * Optional because a page can outlive the server that served it: during a
   * two-slot deploy a reloaded tab gets the new bundle and keeps talking to
   * the old instance, which knows nothing about a flag added after it booted.
   */
  recon?: { providerEvents: boolean };
  /** Which voices a room may be opened with (task 6). Optional for the same reason. */
  voice?: { providers: string[]; models?: Record<string, string[]> };
  /** Rates the creator's call screen turns usage into a running cost with. Hosts only. */
  pricing?: Pricing;
  /**
   * Whether this browser has entered the demo code (task 18). Optional for the
   * same reason as the rest: an older instance never says, and then the page
   * behaves as it always did.
   */
  host?: boolean;
}

export interface Pricing {
  translateUsdPerMin: number;
  transcribeUsdPerMin: number;
  voiceUsdPer1kCredits: number;
  voiceCreditsPerChar: number;
}

let pending: Promise<Capabilities | null> | null = null;
let known: Capabilities | null = null;

export function loadCapabilities(): Promise<Capabilities | null> {
  pending ??= fetch('/api/capabilities')
    .then((r) => (r.ok ? (r.json() as Promise<Capabilities>) : null))
    .then((caps) => {
      known = caps;
      return caps;
    })
    .catch(() => null);
  return pending;
}

/**
 * Asks again. Signing in or out changes the answer — whether this browser is
 * a host, and the prices only a host is given — so the cached one is dropped.
 */
export function reloadCapabilities(): Promise<Capabilities | null> {
  pending = null;
  known = null;
  return loadCapabilities();
}

/**
 * The answer if it has already arrived, and `null` if it has not.
 *
 * For callers that may not wait. The one today is the translator's recon
 * switch, on the path that starts a provider session: a debugging aid is never
 * worth a millisecond of a call's set-up time, so "not known yet" reads as
 * "off". In practice the shell asks for this at mount and a call begins after
 * a room, an invite and a microphone check, so it is always known by then.
 */
export function capabilitiesNow(): Capabilities | null {
  return known;
}
